import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as api from "../api";
import { ApiError } from "../api";
import { Logo } from "../components/ui";
import { useSession } from "../session";
import { useT, useTx } from "../i18n";
import type { Cheie } from "../i18n";

const REASONS: Record<string, Cheie> = {
  not_found: "invitatie.inexistenta",
  revoked: "invitatie.anulata",
  already_accepted: "invitatie.folosita",
  expired: "invitatie.expirata",
};

export default function AcceptInvite() {
  const { token = "" } = useParams();
  const { setUser } = useSession();
  const navigate = useNavigate();
  const t = useT();
  const tx = useTx();

  const [preview, setPreview] = useState<api.InvitationPreview | null>(null);
  const [problem, setProblem] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.previewInvitation(token).then(setPreview).catch((err) =>
      setProblem(t(err instanceof ApiError
        ? REASONS[err.code] ?? "invitatie.nefolosibila"
        : "invitatie.nefolosibila")),
    );
  }, [token, t]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      setUser(await api.acceptInvitation(token, password, fullName.trim()));
      navigate("/projects", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError && err.code === "weak_password"
        ? err.message
        : t("invitatie.esec"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="split">
      <div className="pitch">
        <Logo size={26} />
        <div>
          <h1 className="disp">{t("invitatie.pitch_titlu")}</h1>
          <p>{t("invitatie.pitch_text")}</p>
        </div>
        <span className="lbl">{t("login.beta")}</span>
      </div>

      <div className="form-side">
        {problem && (
          <div className="box">
            <h2 className="disp">{t("invitatie.titlu")}</h2>
            <p className="note bad">{problem}</p>
          </div>
        )}

        {!problem && !preview && <p className="note">{t("invitatie.se_verifica")}</p>}

        {preview && (
          <form onSubmit={submit}>
            <h2 className="disp">{t("invitatie.creeaza_cont")}</h2>
            <p className="note" style={{ marginBottom: 16 }}>
              {tx("invitatie.in_org", { organizatie: preview.organization.name,
                                        rol: preview.role })}
            </p>

            <label className="field">
              <span className="lbl">{t("comun.email")}</span>
              <input type="email" value={preview.email} readOnly />
            </label>

            <label className="field">
              <span className="lbl">{t("comun.nume")}</span>
              <input value={fullName} autoFocus onChange={(e) => setFullName(e.target.value)} />
            </label>

            <label className="field">
              <span className="lbl">{t("invitatie.parola_noua")}</span>
              <input type="password" autoComplete="new-password" value={password}
                     required minLength={10} onChange={(e) => setPassword(e.target.value)} />
            </label>

            {error && <p className="note bad" style={{ marginTop: 12 }}>{error}</p>}

            <div style={{ marginTop: 16 }}>
              <button className="btn btnp wide" type="submit" disabled={busy}>
                {t(busy ? "invitatie.se_creeaza" : "invitatie.buton")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
